import {
    DUE_DATE_GROUP_TITLES,
    type DueDateGroup,
    getDueDateGroup,
    ORDERED_DUE_DATE_GROUPS,
} from "@components/tasks/taskListUtils";
import type { ClientsWorkOverviewWorkRow } from "./clientsWorkOverviewViewConfig";

/** A group of work rows: a List group or a Board column. */
export interface WorkRowGroup {
    id: string;
    title: string;
    rows: ClientsWorkOverviewWorkRow[];
}

/**
 * Work rows by work end: every bucket in List's order, empty ones included,
 * rows in input order.
 */
export const getWorkDueDateGroups = (
    rows: readonly ClientsWorkOverviewWorkRow[],
): WorkRowGroup[] => {
    const rowsByGroup = new Map<DueDateGroup, ClientsWorkOverviewWorkRow[]>(
        ORDERED_DUE_DATE_GROUPS.map((group) => [group, []]),
    );
    for (const row of rows) {
        rowsByGroup.get(getDueDateGroup(row.workDeliveryDate))?.push(row);
    }
    return ORDERED_DUE_DATE_GROUPS.map((group) => ({
        id: `due_date:${group}`,
        title: DUE_DATE_GROUP_TITLES.get(group) ?? group,
        rows: rowsByGroup.get(group) ?? [],
    }));
};

/**
 * Work rows by template, one group per template in the order rows first use
 * it, titled by the template's name.
 */
export const getWorkTemplateGroups = ({
    rows,
    templateNames,
}: {
    rows: readonly ClientsWorkOverviewWorkRow[];
    templateNames: ReadonlyMap<string, string>;
}): WorkRowGroup[] => {
    const groupsByTemplateId = new Map<string, WorkRowGroup>();
    for (const row of rows) {
        const templateId = row.closeTemplateId;
        const group = groupsByTemplateId.get(templateId) ?? {
            id: `template:${templateId}`,
            title: templateNames.get(templateId) ?? "Unknown template",
            rows: [],
        };
        group.rows.push(row);
        groupsByTemplateId.set(templateId, group);
    }
    return [...groupsByTemplateId.values()];
};
